/** Router entry for TanStack Start. */
import { createRouter } from "@tanstack/react-router";

import AppErrorComponent from "./routing/AppErrorComponent";
import { routeTree } from "./routeTree.gen";
import {
  loadPagesAsDocuments,
  navigatesByDocument,
} from "./routing/documentNavigation";
import { marketingHomeHistory } from "./routing/marketingHome";
import { parseSearch, stringifySearch } from "./routing/searchParams";

export function getRouter() {
  const router = createRouter({
    routeTree,
    // Replaces `pages/_error.page.tsx`: our own error screen plus the Sentry
    // report, instead of TanStack's default UI which reports nothing.
    defaultErrorComponent: AppErrorComponent,
    // Matches the Next.js default: the browser restores scroll on navigation.
    scrollRestoration: true,
    // `skipTrailingSlashRedirect: true` in the deleted next.config.ts: Next
    // served `/login/` itself rather than redirecting. The router's default is
    // `never`, which would 307 every such URL.
    trailingSlash: "preserve",
    // Next's `<Link>` prefetched every in-viewport target in production and no
    // call site opted out, so a click cost the destination nothing. `false` —
    // TanStack's default, carried over unexamined — dropped that: `dashboard`
    // -> `$planId/proxy/list` went from 0 requests to 31 (194kB gz). `"viewport"`
    // restores it; `"intent"` is hover, a different trigger.
    //
    // Closest available, not identical (ANO-126): TanStack issues a dynamic
    // `import()` on intersection where Next wrote `<link rel=prefetch>` and
    // deferred execution to `requestIdleCallback`, so the modules — and
    // `getStripe`'s module-scope `loadStripe()` — run at ~500ms against Next's
    // ~1019ms. Vite's finer chunking also preloads 49 chunks at rest to Next's
    // 6, for slightly fewer bytes.
    defaultPreload: "viewport",
    // The router transports search values as opaque strings, exactly as
    // `router.query` does today. JSON encoding stays the caller's business.
    parseSearch,
    stringifySearch,
    // `/` on the marketing host is the home page. Its browser history reads
    // and writes the address that way; the router itself takes no `rewrite`,
    // which would answer a query holding a space with a redirect to itself on
    // every host. Everywhere else this is undefined and the default stands.
    // See `marketingHome.ts`.
    history: marketingHomeHistory(),
  });
  // On the marketing host a link to another page loads it as a new document,
  // as the published site's did; see `documentNavigation.ts`.
  if (navigatesByDocument()) loadPagesAsDocuments(router);
  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
